// Screens around the night: the beach at dawn, the stained-glass windows, the
// cove, the shop, the tide almanac, the shell book and the 宿替え loadout.
// Everything is local to this browser; purchases use prototype currency only.
import { h, moonIcon, shellIcon, driftIcon } from './dom.js';
import { SHELLS, DEFAULT_SHELLS } from '../sim/shells.js';
import { nightInfo } from '../sim/night.js';
import { CRABS } from '../sim/content.js';
import { renderNight, patternSwatch, emptyWindow } from '../meta/glass.js';
import { RECIPES, DECOR_MAX, canBuild, build, coveLight, SHOP, offerLantern, buyPattern, buyAlmanac, DRIFT, LITTLES } from '../meta/profile.js';
import { moonriseMinutes, hhmm } from '../meta/moonrise.js';
import { phaseIndex } from '../sim/moon.js';

const addDays = (iso, n) => new Date(Date.parse(iso + 'T12:00:00Z') + n * 86400000).toISOString().slice(0, 10);

export function createScreens(ctx) {
  const { ui, t, profile, save, today } = ctx;
  const back = (label = t('meta.back')) => h('button', { class: 'chip back', onclick: () => ctx.home() }, label);
  const panel = (cls, ...kids) => { ui.replaceChildren(h('div', { class: `meta-screen ${cls}` }, ...kids)); };
  const coins = () => h('div', { class: 'coins' }, t('meta.coins', { n: profile.coins }), h('span', { class: 'dim' }, ' ' + t('meta.proto')));

  // ------------------------------------------------------------------ rules chips (used on title + HUD)
  const rulesLine = (okite, { long = false } = {}) => h('div', { class: 'rules' },
    okite.rules.map(id => h('div', { class: `rule rule-${id}` },
      h('span', { class: 'rule-name' }, t('rule.' + id + '.name')),
      long ? h('span', { class: 'rule-desc' }, t('rule.' + id + '.desc')) : null)));

  // ------------------------------------------------------------------ beach at dawn
  function showBeach({ finds, onDone }) {
    // Every find lies face down on the sand; tap one to turn it, or pick them all up at once.
    let shown = 0;
    const cards = finds.map((f, k) => {
      const card = h('button', { class: 'find hidden', onclick: () => turn(k) }, h('div', { class: 'find-back' }, '？'));
      card.f = f;
      return card;
    });
    const list = h('div', { class: 'finds' }, cards);
    const next = h('button', { class: 'primary' }, t('beach.next'));
    const turn = k => {
      const card = cards[k];
      if (!card.classList.contains('hidden')) return;
      const f = card.f;
      const icon = f.type === 'shell' ? shellIcon(SHELLS[f.id].glass, 48, SHELLS[f.id].rarity, f.id) : driftIcon(f.type === 'pearl' ? 'pearl' : f.id, 48);
      const name = f.type === 'shell' ? t('shellName.' + f.id) : f.type === 'pearl' ? t('item.pearl') : t('item.' + f.id);
      const note = f.type === 'shell' ? t('trait.' + SHELLS[f.id].trait) : `×${f.n}`;
      const rare = f.type === 'shell' && SHELLS[f.id].rarity >= 2;
      card.className = `find ${rare ? 'rare' : ''} turned`;
      card.replaceChildren(icon, h('div', {}, h('div', { class: 'find-name' }, name), h('div', { class: 'find-note' }, note)));
      ctx.audio?.saved(shown++ % 5);
      if (shown >= finds.length) next.textContent = t('beach.done');
    };
    next.onclick = () => {
      if (shown >= finds.length) { onDone(); return; }
      cards.forEach((_, k) => setTimeout(() => turn(k), k * 160));
    };
    panel('beach',
      h('h2', {}, t('beach.title')),
      h('p', { class: 'dim' }, t('beach.line', { n: finds.length })),
      list, h('div', { class: 'actions' }, next));
  }

  // ------------------------------------------------------------------ stained glass
  function nightLabel(n) {
    const [y, m, d] = n.date.split('-');
    return `${t('date', { y, m: +m, d: +d })} · ${t('tide.' + n.tide)}${n.game ? ' · ' + t('mode.' + n.game) : ''}`;
  }
  function showGlass(night, { onDone } = {}) {
    const caption = night.game && night.totals ? t('glass.caption.' + night.game, { n: night.totals.saved }) : '';
    const narrow = innerWidth < 640;   // phones: a squarer 2-by-2 window that fills the width
    const cv = renderNight(night, { ppc: 8, label: nightLabel(night), caption, cols: narrow ? 2 : 0 });
    cv.className = 'glass-canvas';
    const save2 = h('a', { class: 'chip', download: `tsukinoura-${night.date}.png`, href: cv.toDataURL('image/png') }, t('glass.save'));
    // Phones: the share sheet (save to Photos, send) when it takes files; otherwise the plain download.
    save2.onclick = e => {
      if (!navigator.canShare) return;
      e.preventDefault();
      cv.toBlob(blob => {
        const file = new File([blob], `tsukinoura-${night.date}.png`, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) navigator.share({ files: [file], title: t('glass.title') }).catch(() => {});
        else { const a = h('a', { download: file.name, href: cv.toDataURL('image/png') }); a.click(); }
      });
    };
    panel('glass',
      h('h2', {}, t('glass.title')),
      h('p', { class: 'dim' }, t('glass.line')),
      h('div', { class: 'glass-frame' }, cv),
      // Keep or share first; the real-goods order comes after.
      h('div', { class: 'actions' },
        Object.assign(save2, { className: 'primary' }),
        h('button', { class: 'chip', onclick: () => showOrder(night) }, t('glass.order')),
        h('button', { class: 'chip', onclick: () => (onDone ? onDone() : ctx.home()) }, t('meta.back'))));
  }
  function showOrder(night) {
    const cv = renderNight(night, { ppc: 4 });
    const url = cv.toDataURL('image/png');
    const products = [
      { id: 'coaster', price: 2400 }, { id: 'washi', price: 3800 }, { id: 'suncatcher', price: 6800 },
    ];
    panel('order',
      h('h2', {}, t('order.title')),
      h('p', { class: 'dim' }, t('order.line')),
      h('div', { class: 'products' }, products.map(p => h('div', { class: `product product-${p.id}` },
        h('div', { class: 'mock', style: `background-image:url(${url})` }),
        h('div', { class: 'product-name' }, t('order.' + p.id)),
        h('div', { class: 'product-price' }, `¥${p.price.toLocaleString()}`),
        h('button', { class: 'chip', disabled: true }, t('order.soon'))))),
      h('div', { class: 'actions' }, h('button', { class: 'chip', onclick: () => showGallery() }, t('meta.back'))));
  }
  function showGallery() {
    const nights = profile.nights;
    const week = nights.slice(0, 7);
    panel('gallery',
      h('h2', {}, t('gallery.title')),
      h('p', { class: 'dim' }, nights.length ? t('gallery.line', { n: nights.length }) : t('gallery.empty')),
      nights.length ? null : h('div', { class: 'glass-frame' }, Object.assign(emptyWindow(phaseIndex(ctx.today)), { className: 'glass-canvas' })),
      week.length ? h('div', { class: 'week' },
        h('div', { class: 'week-title' }, t('gallery.week', { n: week.length })),
        h('div', { class: 'week-panes' }, week.slice().reverse().map(n => { const c = renderNight(n, { ppc: 2 }); c.className = 'week-pane'; c.title = nightLabel(n); c.onclick = () => showGlass(n, { onDone: showGallery }); return c; }))) : null,
      h('div', { class: 'gallery-list' }, nights.map(n => h('button', { class: 'gallery-item', onclick: () => showGlass(n, { onDone: showGallery }) },
        moonIcon(n.phase, 22), h('span', {}, nightLabel(n)), h('span', { class: 'dim' }, ` ${n.totals.saved}/${n.totals.total}`)))),
      h('div', { class: 'actions' }, back()));
  }

  // ------------------------------------------------------------------ the cove
  function showCove() {
    ctx.enterCove();
    const row = id => {
      const can = canBuild(profile, id);
      const cost = Object.entries(RECIPES[id]).map(([k, n]) => h('span', { class: 'cost' }, driftIcon(k === 'pearls' ? 'pearl' : k, 18), `×${n}`));
      return h('div', { class: 'decor' },
        h('div', { class: 'decor-name' }, t('decor.' + id), h('span', { class: 'dim' }, ` ${profile.decor[id]}/${DECOR_MAX[id]}`)),
        h('div', { class: 'decor-cost' }, cost),
        h('button', { class: 'chip', disabled: !can, onclick: () => { if (build(profile, id)) { save(); ctx.coveChanged(); showCove(); ctx.audio?.poolClear(); } } }, can ? t('decor.build') : profile.decor[id] >= DECOR_MAX[id] ? t('decor.full') : t('decor.short')));
    };
    const res = LITTLES.map(k => h('span', { class: 'res' }, t('little.' + k), ` ${profile.residents[k]}`));
    const drift = [...DRIFT.map(k => h('span', { class: 'res' }, driftIcon(k, 18), ` ${profile.drift[k]}`)), h('span', { class: 'res' }, driftIcon('pearl', 18), ` ${profile.pearls}`)];
    ui.replaceChildren(h('div', { class: 'cove-ui' },
      h('div', { class: 'cove-panel' },
        h('h2', {}, t('cove.title')),
        h('div', { class: 'cove-light' }, t('cove.light'), h('div', { class: 'bar' }, h('div', { class: 'fill', style: `width:${Math.round(coveLight(profile) * 100)}%` }))),
        h('div', { class: 'dim small' }, t('cove.residents')), h('div', { class: 'res-row' }, res),
        h('div', { class: 'dim small' }, t('cove.finds')), h('div', { class: 'res-row' }, drift),
        ['lantern', 'netfloat', 'noren', 'boat'].map(row),
        h('div', { class: 'actions' }, h('button', { class: 'chip', onclick: () => { ctx.leaveCove(); ctx.home(); } }, t('meta.back'))))));
  }

  // ------------------------------------------------------------------ shop and lanterns
  function showShop(msg = '') {
    const month = today.slice(0, 7);
    const mine = profile.lanterns.find(l => l.month === month);
    const nameInput = h('input', { class: 'name-input', maxlength: 12, placeholder: t('shop.lanternName') });
    const count = ctx.lanternSample + profile.lanterns.filter(l => l.month === month).length;
    const goal = ctx.lanternGoal;
    panel('shop',
      h('h2', {}, t('shop.title')), coins(),
      msg ? h('div', { class: 'msg' }, msg) : null,
      h('section', { class: 'shop-card lantern-card' },
        h('h3', {}, t('shop.lantern')),
        h('p', {}, t('shop.lanternLine')),
        h('div', { class: 'goal' }, t('shop.lanternCount', { n: count.toLocaleString(), goal: goal.toLocaleString() }), h('span', { class: 'dim' }, ' ' + t('shop.sample')),
          h('div', { class: 'bar' }, h('div', { class: 'fill', style: `width:${Math.min(100, (count / goal) * 100)}%` }))),
        mine ? h('div', { class: 'mine' }, t('shop.lanternMine', { name: mine.name }))
          : h('div', { class: 'buy-row' }, nameInput, h('button', { class: 'primary', onclick: () => {
            if (offerLantern(profile, nameInput.value, month)) { save(); ctx.audio?.calm(); showShop(t('shop.lanternDone')); } else showShop(t('shop.cannot'));
          } }, t('shop.offer', { price: SHOP.lantern.price })))),
      h('section', { class: 'shop-card' },
        h('h3', {}, t('shop.patterns')), h('p', {}, t('shop.patternsLine')),
        h('div', { class: 'buy-grid' }, ['patternKiriko', 'patternShibori', 'patternKinran'].map(id => {
          const owned = profile.owned.patterns.includes(SHOP[id].pattern);
          return h('div', { class: 'buy-item' }, (profile.nights[0] ? renderNight({ ...profile.nights[0], pattern: SHOP[id].pattern }, { ppc: 3 }) : patternSwatch(SHOP[id].pattern)), h('div', {}, t('pattern.' + SHOP[id].pattern)),
            h('button', { class: 'chip', disabled: owned, onclick: () => { if (buyPattern(profile, id)) { save(); showShop(t('shop.bought')); } else showShop(t('shop.cannot')); } }, owned ? t('shop.owned') : t('shop.buy', { price: SHOP[id].price })));
        }))),
      h('section', { class: 'shop-card' },
        h('h3', {}, t('shop.almanac')), h('p', {}, t('shop.almanacLine')),
        profile.almanac ? h('div', { class: 'mine' }, t('shop.almanacOn'))
          : h('button', { class: 'chip', onclick: () => { if (buyAlmanac(profile)) { save(); showShop(t('shop.bought')); } else showShop(t('shop.cannot')); } }, t('shop.buy', { price: SHOP.almanac.price }))),
      h('section', { class: 'shop-card' }, h('h3', {}, t('shop.glass')), h('p', {}, t('shop.glassLine')),
        h('button', { class: 'chip', onclick: () => showGallery() }, t('shop.toGallery'))),
      h('p', { class: 'dim small' }, t('shop.noGacha')),
      h('div', { class: 'actions' }, back()));
  }

  // ------------------------------------------------------------------ tide almanac
  function showAlmanac() {
    const days = Array.from({ length: 7 }, (_, k) => addDays(today, k));
    panel('almanac',
      h('h2', {}, t('almanac.title')),
      h('p', { class: 'dim' }, profile.almanac ? t('almanac.lineOn') : t('almanac.lineOff')),
      h('div', { class: 'almanac-list' }, days.map((d, k) => {
        const info = nightInfo(d);
        const open = k === 0 || profile.almanac;
        const [, m, dd] = d.split('-');
        const suits = Object.entries(SHELLS).filter(([, s]) => s.tags.some(tag => info.okite.rules.includes(tag))).map(([id]) => id);
        return h('div', { class: `almanac-day ${open ? '' : 'locked'}` },
          h('div', { class: 'al-date' }, moonIcon(info.phase, 26), ` ${+m}/${+dd}`, h('span', { class: 'dim' }, ' ' + t('tide.' + info.tide)), h('span', { class: 'al-game' }, ' ' + t('mode.' + info.mode))),
          open ? h('div', {},
            rulesLine(info.okite),
            h('div', { class: 'al-meta dim small' }, t('almanac.moonrise', { time: hhmm(moonriseMinutes(d)) }), info.rules.boss ? ' · ' + t('almanac.boss') : ''),
            suits.length ? h('div', { class: 'al-suits' }, t('almanac.suits'), suits.map(id => h('span', { class: 'suit' }, shellIcon(SHELLS[id].glass, 16, 0, id), t('shellName.' + id)))) : null)
            : h('div', { class: 'dim' }, t('almanac.locked')));
      })),
      h('div', { class: 'actions' }, profile.almanac ? null : h('button', { class: 'chip', onclick: () => showShop() }, t('almanac.toShop')), back()));
  }

  // ------------------------------------------------------------------ shell book
  function showShells() {
    panel('shells',
      h('h2', {}, t('shells.title')),
      h('p', { class: 'dim' }, t('shells.line', { n: Object.keys(profile.shells).length, total: Object.keys(SHELLS).length })),
      h('div', { class: 'shell-grid' }, Object.entries(SHELLS).map(([id, s]) => {
        const n = profile.shells[id] || 0;
        return h('div', { class: `shell-card ${n ? '' : 'unknown'} rarity-${s.rarity}` },
          shellIcon(n ? s.glass : '#2a2836', 44, n ? s.rarity : 0, n ? id : null),
          h('div', { class: 'shell-name' }, n ? t('shellName.' + id) : '？？？'),
          h('div', { class: 'shell-trait' }, n ? t('trait.' + s.trait) : t('shells.unknown')),
          h('div', { class: 'dim small' }, t('rarity.' + s.rarity) + (n > 1 ? ` ×${n}` : '')));
      })),
      h('div', { class: 'actions' }, back()));
  }

  // ------------------------------------------------------------------ 磯めぐり: any night, your own shells
  function showMeguri(state = { date: addDays(today, -1) }) {
    const info = nightInfo(state.date);
    const [y, m, d] = state.date.split('-');
    const owned = Object.keys(profile.shells).filter(id => profile.shells[id] > 0);
    const suits = id => SHELLS[id].tags.some(tag => info.okite.rules.includes(tag) || (tag === 'boss' && info.rules.boss));
    panel('meguri',
      h('h2', {}, t('meguri.title')),
      h('p', { class: 'dim' }, t('meguri.line')),
      h('div', { class: 'date-pick' },
        h('button', { class: 'chip', onclick: () => showMeguri({ date: addDays(state.date, -1) }) }, '◀'),
        h('div', { class: 'date-now' }, moonIcon(info.phase, 28), ` ${t('date', { y, m: +m, d: +d })} · ${t('tide.' + info.tide)} · ${t('mode.' + info.mode)}`),
        h('button', { class: 'chip', disabled: state.date >= today, onclick: () => showMeguri({ date: addDays(state.date, 1) }) }, '▶')),
      rulesLine(info.okite, { long: true }),
      h('h3', {}, t('meguri.loadout')),
      h('div', { class: 'loadout' }, CRABS.map((c, i) => h('div', { class: `load seat-${i}` },
        h('div', { class: 'load-crab' }, t('crab.' + c.id)),
        h('select', { class: 'load-select', onchange: e => { profile.loadout[c.id] = e.target.value; save(); showMeguri(state); } },
          owned.map(id => h('option', { value: id, selected: profile.loadout[c.id] === id ? 'selected' : false }, `${t('shellName.' + id)}${suits(id) ? ' ★' : ''}`))),
        h('div', { class: 'load-trait dim small' }, t('trait.' + SHELLS[profile.loadout[c.id]]?.trait))))),
      h('p', { class: 'dim small' }, t('meguri.star')),
      h('div', { class: 'pattern-pick' }, t('meguri.pattern'),
        h('select', { class: 'load-select', onchange: e => { profile.patternOn = e.target.value; save(); } },
          h('option', { value: '' }, t('meguri.noPattern')),
          profile.owned.patterns.map(id => h('option', { value: id, selected: profile.patternOn === id ? 'selected' : false }, t('pattern.' + id))))),
      h('div', { class: 'actions' },
        h('button', { class: 'primary', onclick: () => ctx.startMeguri(state.date, { ...profile.loadout }) }, t('meguri.go')),
        h('button', { class: 'chip', onclick: () => { profile.loadout = { ...DEFAULT_SHELLS }; save(); showMeguri(state); } }, t('meguri.reset')),
        back()));
  }

  return { rulesLine, showBeach, showGlass, showGallery, showCove, showShop, showAlmanac, showShells, showMeguri };
}
